"use client"

import { CheckIconSVG } from "@/src/ui/AllSVG"
import { formatAmount } from "@/src/utils/formatters"
import { Button } from "@heroui/react"
import { useRouter } from "nextjs-toploader/app"
import { getCategoriesPath } from "@/src/routes/routes"
import { useBuyMembershipMutation } from "@/src/store/rtkQueries/userPostAPI"
import { useRazorpayCheckout } from "../cart/RazorpayCheckout"
import { useCallback } from "react"
import { toast } from "@/src/lib/toast"
import { useDispatch } from "react-redux"
import { openDrawer } from "@/src/store/slices/allDrawerSlice"
import { getAuthToken } from "@/src/utils/authCookies"
import { useAuth } from "@/src/hooks/useAuth"
import { useGetUserProfileDataQuery } from "@/src/store/rtkQueries/userGetAPI"

const ESSENTIAL_FEATURES = [
    "Access to the online store",
]

const PREMIER_FEATURES = [
    "Members only pricing",
    "Exclusive discounts",
    "Reduced Shipping Charges",
    "Access to Exclusive Products",
]

function formatMembershipEndDate(isoDate: string) {
    const d = new Date(isoDate)
    if (Number.isNaN(d.getTime())) return isoDate
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

interface PlanCardProps {
    title: string
    subtitle: string
    price: string
    priceNote: string
    featuresLabel: string
    features: string[]
    isPrimary?: boolean
    amount: number
    currency: string
    billingCycle: "yearly"
    isActive?: boolean
    membershipEndDate?: string
}

function PlanCard({ title, subtitle, price, priceNote, featuresLabel, features, isPrimary, amount, currency, billingCycle, isActive, membershipEndDate }: PlanCardProps) {
    const router = useRouter();
    const dispatch = useDispatch()
    const { isProcessing, startCheckout } = useRazorpayCheckout()
    const [buyMembership, { isLoading: isBuyMembershipLoading }] = useBuyMembershipMutation()
    const isLoading = isProcessing || isBuyMembershipLoading
    console.log('price', price);
    console.log('isActive', isActive);

    const handleBuyMembership = useCallback(async () => {
        if (!getAuthToken()) {
            dispatch(openDrawer({ componentName: 'AuthDrawer', placement: 'right', className: 'rounded-none w-[500px]' }))
            return
        }
        await startCheckout(amount, {
            onVerified: async (razorpayResponse) => {
                try {
                    const response = await buyMembership({
                        plan_name: title,
                        billing_cycle: billingCycle,
                        membership_price: amount,
                        currency: currency,
                        payment_method: 'razorpay',
                        transaction_id: razorpayResponse.razorpay_payment_id,
                        payment_status: 'paid',
                        start_date: new Date().toISOString(),
                    }).unwrap()
                    if (response.http_status_code === 200 || response.http_status_code === 201) {
                        toast.success(response.message || 'Membership activated successfully!')
                    } else {
                        toast.danger(response.message || 'Could not activate membership')
                    }
                } catch (error) {
                    console.error('Error buying membership')
                }
            },
        })
    }, [amount, currency, dispatch, startCheckout, buyMembership])

    return (
        <div className={`flex flex-col rounded-2xl border p-7 gap-5 ${isPrimary ? "bg-white border-6 border-muted-color" : "bg-transparent border-muted-color"}`}>
            <div className="space-y-1.5">
                <h3 className="text-xl font-medium">{title}</h3>
                <p className="text-sm text-body-color/60 font-light">{subtitle}</p>
            </div>

            <hr className="border-muted-color" />

            <div className="space-y-1">
                <p className="tertiary_heading font-medium">{price}</p>
                <p className="text-sm text-body-color/60">{priceNote}</p>
            </div>

            <div className="space-y-3 flex-1">
                <p className="text-sm text-body-color">{featuresLabel}</p>
                <ul className="space-y-2.5">
                    {features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2.5 text-sm font-light">
                            <div className="bg-[#F4F6F7] h-5 w-5 text-body-color rounded-full flex items-center justify-center"><CheckIconSVG /></div>
                            {feature}
                        </li>
                    ))}
                </ul>
            </div>

            {price == 'Free' ? (isActive ? null : <Button
                className={`global_btn rounded_full w-full font-normal! bg-[#F4F6F7]! text-body-color!`}
                onPress={() => router.push(getCategoriesPath())}
            >
                CURRENT MEMBERSHIP
            </Button>
            ) :
                (<>
                    {isActive ? <div className="space-y-2 w-full">
                        <Button
                            className={`global_btn rounded_full w-full font-normal! bg_primary uppercase`}
                        >
                            Current Membership
                        </Button>
                        {membershipEndDate ? (
                            <p className="text-center text-sm text-body-color/70 font-light">
                                Valid until {formatMembershipEndDate(membershipEndDate)}
                            </p>
                        ) : null}
                    </div> : <Button
                        className={`global_btn rounded_full w-full font-normal! bg_primary uppercase`}
                        isDisabled={isLoading}
                        isPending={isLoading}
                        onPress={handleBuyMembership}
                    >
                        GET STARTED NOW
                    </Button>}
                </>)}
        </div>
    )
}

interface MembershipCardProps {
    membershipPrice: number;
    membershipCurrency: string;
    initialIsAuthenticated?: boolean;
}

export default function MembershipCard({ membershipPrice, membershipCurrency, initialIsAuthenticated }: MembershipCardProps) {
    const { isAuthenticated } = useAuth(initialIsAuthenticated)
    const { data: profileData } = useGetUserProfileDataQuery(undefined, { skip: !isAuthenticated })
    const user = profileData?.data
    // console.log('user', user);

    const activeMembership = user?.activeMembership ? user?.activeMembership : false
    const membership = user?.membership
    const endDateForPlan = (planName: string) =>
        activeMembership && membership?.plan_name === planName ? membership.end_date : undefined
    return (
        <>
            <div className="w-[90%] sm:w-[80%] md:w-[70%] lg:w-[60%] mx-auto">
                <div className="flex justify-center mb-6">
                    <div className="bg-body-color text-white rounded-full px-5 py-2 text-sm w-fit">
                        Yearly
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <PlanCard
                        title="Essential"
                        subtitle="Perfect for the modern man starting his style journey"
                        price={'Free'}
                        priceNote={'no commitment required'}
                        amount={0}
                        currency={membershipCurrency}
                        billingCycle="yearly"
                        featuresLabel="What's included:"
                        features={ESSENTIAL_FEATURES}
                        isActive={activeMembership}
                        membershipEndDate={endDateForPlan('Essential')}
                    />
                    <PlanCard
                        title="Premier"
                        subtitle="For the discerning gentleman who expects the finest"
                        price={formatAmount(membershipPrice, membershipCurrency)}
                        priceNote={'per year, billed annually'}
                        amount={membershipPrice}
                        currency={membershipCurrency}
                        billingCycle="yearly"
                        featuresLabel="Everything in Essential, plus:"
                        features={PREMIER_FEATURES}
                        isPrimary
                        isActive={activeMembership}
                        membershipEndDate={endDateForPlan('Premier')}
                    />
                </div>
            </div>
        </>
    )
}
